import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { WaterfallStream, WaterBackground } from '../components/Backgrounds';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login, signup, error: authError, isLoading, loginAsGuest } = useAuthStore();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): boolean => {
    // Password should be at least 6 characters long
    return password.length >= 6;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (isSignup && !validatePassword(password)) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    try {
      if (isSignup) {
        await signup(email, password);
      } else {
        await login(email, password);
      }
      navigate('/dashboard');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-blue-500 via-blue-600 to-blue-800 text-white flex items-center justify-center p-4 font-sans overflow-hidden">
      <WaterBackground />
      <WaterfallStream />

      <div className="relative z-10 w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="absolute -top-12 left-0 text-white/80 hover:text-white flex items-center transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          Back to Home
        </button>

        <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-2xl p-8 w-full border border-white/50 animate-fade-in-up">
          <div className="flex flex-col items-center justify-center mb-8">
            <div className="bg-blue-100 p-4 rounded-full mb-4 shadow-inner">
              <img src="/hydrio-icon.png" alt="Hydrio Logo" className="h-10 w-10 text-blue-60 drop-shadow-sm" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Hydrio</h1>
            <p className="text-gray-600 mt-2 text-sm">
              {isSignup ? 'Create your account' : 'Welcome back'}
            </p>
          </div>

          {(error || authError) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error || authError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 ml-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-gray-900 placeholder-gray-400 transition-all"
                required
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 ml-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-gray-900 placeholder-gray-400 transition-all"
                required
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-md font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 mt-2"
            >
              {isLoading ? 'Loading...' : (isSignup ? 'Create Account' : 'Sign In')}
            </button>

            <div className="relative flex py-2 items-center">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase tracking-wider">Or</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            <button
              type="button"
              onClick={() => {
                loginAsGuest();
                navigate('/dashboard');
              }}
              disabled={isLoading}
              className="w-full flex justify-center py-3.5 px-4 border border-gray-200 rounded-xl shadow-sm text-md font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 disabled:opacity-70 transition-all duration-200"
            >
              Continue as Guest
            </button>

            <div className="text-center mt-6">
              <button
                type="button"
                onClick={() => setIsSignup(!isSignup)}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors font-medium"
              >
                {isSignup ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};